import React from 'react';
import ReactDOM from 'react-dom';
import DesktopDropdownMenu from '../homepage_components/desktop_dropdown_menu';
import EmbeddedMenu from '../homepage_components/embedded_menu';
import MemberStarTooltip from '../homepage_components/member_star_tooltip';
import axios from 'axios';
const post = axios.post;
import { xhrConfig } from 'form_components/apis/api';
import { allowNavigationWithUnsavedChanges } from 'form_components/hooks/use_unsaved_alert';

export const abandonListing = async (returnPath = '/') => {
  const _response = await post('/post/listings/abandon', {}, xhrConfig());
  window.location.href = returnPath;
};

export const pluckKeys = (keys, origObj) =>
  keys.reduce((newObj, k) => ({ ...newObj, [k]: origObj[k] }), {});

export const categoryIdFromContext = (context) => {
  return {
    have_space: 0,
    seeking_space: 0,
    opportunity: 1,
    jobs: 1,
  }[context];
};

export const collectBrokerDetails = ({ subcategory, relationship_to_space }) =>
  ['apt_rent', 'house_rent', 'room_rent', 'apt_sale', 'house_sale'].includes(
    subcategory,
  ) && relationship_to_space == 'broker';

export const collectStreetAddress = (attributes) =>
  collectBrokerDetails(attributes);

export const validateEmail = ({ email }) => {
  let errors = {};
  if (!email) {
    errors.email = 'An email address is required';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
    errors.email = 'Invalid email address';
  }
  return errors;
};

export const optionsFor = (list) =>
  list.map((item, index) => (
    <option key={index} value={item[1]}>
      {item[0]}
    </option>
  ));

export const DisplayFormikState = (props) => {
  // return ''
  return (
    <div style={{ margin: '1rem 0' }}>
      <h3 style={{ fontFamily: 'monospace' }} />
      <pre
        style={{
          fontFamily: 'monospace',
          background: '#f6f8fa',
          fontSize: '1rem',
          padding: '.5rem',
          textAlign: 'left',
        }}
      >
        <strong>props</strong> = {JSON.stringify(props, null, 2)}
      </pre>
    </div>
  );
};

const LISTING_CHANGED_MESSAGE =
  'This listing was changed after you opened this page, so your edit was NOT saved.\n\n' +
  'Copy any text you want to keep, then press OK to reload the latest version. ' +
  'Press Cancel to stay on this page.';

const LISTING_NOT_EDITABLE_MESSAGE =
  'This listing can no longer be edited (the deadline is Tuesday at 11:59 PM ET), so your edit was NOT saved.\n\n' +
  'Press OK to go to your listings, or Cancel to stay on this page.';

const errorCode = (error) => error.response?.data?.errors?.[0]?.code;

// 409 and 403 come only from the dashboard edit page: the create/draft flow
// never sends snapshot_content_version and unsubmitted drafts skip the
// deadline check (docs/design/listing-edit-conflict-guard.md). Cancel leaves
// the form as typed, so nothing is lost; saving again just repeats the
// refusal.
const confirmAndLeave = (message, leave) => {
  if (window.confirm(message)) {
    allowNavigationWithUnsavedChanges();
    leave();
  }
};

export const handleFormSubmit = (values, actions, updateAttributes) => {
  updateAttributes(values)
    .then(() => {
      actions.setSubmitting(false);
      actions.resetForm({ values: values });
    })
    .catch((error) => {
      actions.setSubmitting(false);
      const status = error.response && error.response.status;
      if (status === 400) {
        actions.setErrors(error.response.data);
      } else if (status === 409 && errorCode(error) === 'listing_changed') {
        confirmAndLeave(LISTING_CHANGED_MESSAGE, () =>
          window.location.reload(),
        );
      } else if (
        status === 403 &&
        errorCode(error) === 'listing_not_editable'
      ) {
        confirmAndLeave(LISTING_NOT_EDITABLE_MESSAGE, () => {
          window.location.href = '/account/listings';
        });
      } else {
        actions.setErrors('Unknown error submitting form.');
      }
    });
};

export const formatDateMonDayOridinal = (date) => {
  // const date = new Date(Date.parse(dateStr))
  const nth = (n) =>
    ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
  const monthStr = date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
  });
  const day = date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    day: 'numeric',
  });
  const dayStr = day + nth(parseInt(day));
  return monthStr + ' ' + dayStr;
};

export const formatDateMonthDayOridinal = (date) => {
  // const date = new Date(Date.parse(dateStr))
  const nth = (n) =>
    ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
  const monthStr = date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
  });
  const day = date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    day: 'numeric',
  });
  const dayStr = day + nth(parseInt(day));
  return monthStr + ' ' + dayStr;
};
export const loadScript = (src) => {
  let tag = document.createElement('script');
  tag.async = false;
  tag.src = src;
  document.getElementsByTagName('body').appendChild(tag);
};

export const categoryLabelFromSubcategory = (subcategory) =>
  'jobs' == subcategory
    ? 'Jobs & Gigs'
    : 'services' == subcategory
      ? 'Services Offered'
      : 'Real Estate';

export const isHousingSublet = (subcategory) =>
  ['apt_sublet', 'house_sublet', 'room_sublet', 'live_work_sublet'].includes(
    subcategory,
  );

// Kept after the World Cup teardown: the fifa_sublet trait stays on historical
// listings so CX can still see which past listings were confirmed World Cup
// sublets. Drives the ⚽ badge in the newsletter vetting screen.
export const isConfirmedFifaSublet = (traits) => traits.includes('fifa_sublet');

// A listing is a broker listing when the lister picked "broker" as their
// relationship to the space — independent of whether a broker license is on
// file. A license is only required/collected for apt/house/room rentals, so
// broker-listed sublets legitimately have none. Drives the 💼 badge + the
// broker-only admin filter and its counts.
export const isBrokerListing = (listing) =>
  listing.relationshipToSpace === 'broker';

export const setupNav = () => {
  let scrollPos = 0;
  const nav = document.querySelector('.mobileBottomNav');

  function debounce(method, delay) {
    clearTimeout(method._tId);
    method._tId = setTimeout(function () {
      method();
    }, delay);
  }

  function checkPosition() {
    let windowY = window.scrollY;
    if (windowY < 0) windowY = 0;
    if (windowY <= scrollPos) {
      // Scrolling UP
      nav.classList.remove('mobileBottomNav-hidden');
      nav.classList.add('mobileBottomNav-show');
    } else {
      // Scrolling DOWN
      nav.classList.remove('mobileBottomNav-show');
      nav.classList.add('mobileBottomNav-hidden');
    }
    scrollPos = windowY;
  }

  const handleBottomNavScroll = () => debounce(checkPosition, 60);

  document.addEventListener('DOMContentLoaded', () => {
    const desktopDropdownMenuEl = document.getElementById(
      'desktopDropdownMenuRoot',
    );
    if (desktopDropdownMenuEl) {
      ReactDOM.render(<DesktopDropdownMenu />, desktopDropdownMenuEl);
    }
    const embeddedMenuEl = document.getElementById('embeddedMenuRoot');
    if (embeddedMenuEl) {
      ReactDOM.render(<EmbeddedMenu />, embeddedMenuEl);
    }

    // Mount the single shared supporting-member star tooltip only when at least
    // one star is on the page (its anchors are server-rendered). One instance
    // serves every star via anchorSelect, so no per-card React mount is needed.
    if (document.querySelector('.member-star-anchor')) {
      const memberStarTooltipEl = document.createElement('div');
      document.body.appendChild(memberStarTooltipEl);
      ReactDOM.render(<MemberStarTooltip />, memberStarTooltipEl);
    }

    // 1100 pixels wide is current breakpoint
    if (nav && document.documentElement.clientWidth < 1100) {
      window.addEventListener('scroll', handleBottomNavScroll);
    }
  });
  window.onresize = () => {
    if (nav && document.documentElement.clientWidth < 1100) {
      window.addEventListener('scroll', handleBottomNavScroll);
    } else {
      window.removeEventListener('scroll', handleBottomNavScroll);
    }
  };
};

// GA4 save_listing: a demand-side engagement signal, fired from the shared
// bookmark helpers so every entry point (listing detail, browse cards,
// saved-listings dashboard) is covered in one place. Fired only after the
// server confirms the change — like listing_contact, we count the real action,
// not a click that may fail. gtag is guarded because this is a shared module;
// it stays consent-gated by Termly where present.
const trackSaveListing = (action, listing_id, listing_type) => {
  if (typeof gtag === 'function') {
    gtag('event', 'save_listing', {
      action: action,
      item_id: String(listing_id),
      listing_type: listing_type,
    });
  }
};

export const saveBookmark = async (listing_id, user_id, listing_type) => {
  post(
    '/account/bookmarks',
    { bookmark: { listing_id: listing_id, user_id: user_id } },
    xhrConfig(),
  ).then(() => trackSaveListing('save', listing_id, listing_type));
};

export const removeBookmark = async (listing_id, user_id, listing_type) => {
  post(
    '/account/bookmarks/destroy',
    { bookmark: { listing_id: listing_id, user_id: user_id } },
    xhrConfig(),
  ).then(() => trackSaveListing('unsave', listing_id, listing_type));
};

export const toggleNotifyRepost = async (listing_id, user_id) => {
  post(
    '/account/bookmarks/toggle_notify_repost',
    { bookmark: { listing_id: listing_id, user_id: user_id } },
    xhrConfig(),
  );
};
